import React, { createContext, FC, ReactNode, useCallback, useContext, useMemo, useState } from "react";

import { useDateTime } from "@/hooks/useDate";

import { ProgressSnapshotCreateForm } from "../components/progress-snapshot-create-form/internal/progress-snapshot-create-form.schema";

interface CreateProgressSnapshotContextType {
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  newProgressSnapshotForm: ProgressSnapshotCreateForm;
  hasActiveChanges: boolean;
  updateNewProgressSnapshot: (progressSnapshotCreateFormFragment: Partial<ProgressSnapshotCreateForm>) => void;
  resetNewPogressSnapshotForm: () => void;
}

const CreateProgressSnapshotContext = createContext<CreateProgressSnapshotContextType | undefined>(undefined);

interface CreateProgressSnapshotContextProviderProps {
  children: ReactNode;
}

export const CreateProgressSnapshotContextProvider: FC<CreateProgressSnapshotContextProviderProps> = ({ children }) => {
  const { NOW } = useDateTime();
  const defaultValue: ProgressSnapshotCreateForm = useMemo(() => {
    return {
      description: "",
      status: "",
      snapshotDate: NOW.toISOString(),
      keyResultId: "",
      value: "",
      properties: "",
    };
  }, [NOW]);
  const [showModal, setShowModal] = useState(false);
  const [newProgressSnapshotForm, setNewProgressSnapshotForm] = useState<ProgressSnapshotCreateForm>(defaultValue);

  const resetNewPogressSnapshotForm = useCallback(() => {
    setNewProgressSnapshotForm(defaultValue);
  }, [defaultValue]);

  const updateNewProgressSnapshot = useCallback(
    (progressSnapshotCreateFormFragment: Partial<ProgressSnapshotCreateForm>) => {
      const newValues = {
        ...newProgressSnapshotForm,
        ...progressSnapshotCreateFormFragment,
      };
      setNewProgressSnapshotForm(newValues);
    },
    [newProgressSnapshotForm],
  );

  const hasActiveChanges = useMemo(() => {
    if (defaultValue.description !== newProgressSnapshotForm.description) {
      return true;
    }
    if (defaultValue.status !== newProgressSnapshotForm.status) {
      return true;
    }
    if (defaultValue.snapshotDate !== newProgressSnapshotForm.snapshotDate) {
      return true;
    }
    if (defaultValue.keyResultId !== newProgressSnapshotForm.keyResultId) {
      return true;
    }
    if (defaultValue.value !== newProgressSnapshotForm.value) {
      return true;
    }
    if (defaultValue.properties !== newProgressSnapshotForm.properties) {
      return true;
    }
    return false;
  }, [defaultValue, newProgressSnapshotForm]);

  return (
    <CreateProgressSnapshotContext.Provider
      value={{
        showModal,
        setShowModal,
        newProgressSnapshotForm,
        hasActiveChanges,
        resetNewPogressSnapshotForm,
        updateNewProgressSnapshot,
      }}
    >
      {children}
    </CreateProgressSnapshotContext.Provider>
  );
};

// Define the useProjectUpdateUIStateState hook
export const useCreateProgressSnapshotContext = (): CreateProgressSnapshotContextType => {
  const context = useContext(CreateProgressSnapshotContext);

  if (!context) {
    throw new Error("useCreateProgressSnapshotContext must be used within a CreateProgressSnapshotContextProvider");
  }

  return context;
};
